// InterviewForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ScienceIcon from '@mui/icons-material/Science';
import { useTheme } from '@mui/material';
import { showError } from '../../utils/errorHandler';
import { bg, text as textColors, border, adaptiveColor } from '../../utils/darkModeAdapter';
import { saveCommonFields, loadCommonFields } from '../../utils/commonProfileStore';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1.5),
  background: bg(theme).card,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  border: `1px solid ${border(theme).subtle}`,
  maxWidth: 720,
  margin: '0 auto',
  position: 'relative',
  overflow: 'hidden',
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 700,
  color: theme.palette.primary.main,
  fontSize: '1.5rem',
  letterSpacing: '-0.02em',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    backgroundColor: bg(theme).card,
    '&.Mui-focused': {
      boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: textColors(theme).secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    }
  },
  '& .MuiFormHelperText-root': {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: textColors(theme).muted,
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  height: '100%',
  minHeight: '80px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  borderRadius: theme.spacing(1),
  transition: 'box-shadow 0.2s ease',
  backgroundColor: bg(theme).card,
  border: `1px solid ${border(theme).subtle}`,
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.spacing(1),
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  background: theme.palette.primary.main,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  transition: 'background 0.2s ease',
  textTransform: 'none',
  '&:hover': {
    background: theme.palette.primary.dark,
  },
  '&:active': {
    background: theme.palette.primary.dark,
  },
  '&:disabled': {
    background: border(theme).default,
    color: textColors(theme).disabled,
    boxShadow: 'none',
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  backgroundColor: bg(theme).subtle,
  border: `1px solid ${border(theme).subtle}`,
  '&::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    borderRadius: theme.spacing(1),
    '&:hover': {
      backgroundColor: bg(theme).hover,
    }
  }
}));

/**
 * 面接対象ポジションの情報入力フォーム
 * @param {Object} props
 * @param {Function} props.onSubmit - フォーム送信時のハンドラ
 * @param {Object} props.initialValues - フォームの初期値（任意）
 */
function InterviewForm({ onSubmit, initialValues = {}, selectedTemplate = null, onBack }) {
  const theme = useTheme();

  // 面接段階オプションの定義
  const stageOptions = [
    { id: '1次面接', label: '1次面接' },
    { id: '2次面接', label: '2次面接' },
    { id: '3次面接', label: '3次面接' },
    { id: '4次面接', label: '4次面接' },
    { id: '最終面接', label: '最終面接' }
  ];

  // 初期値からチェックボックスの状態を作成
  const initializeStageSelections = () => {
    const initialStages = initialValues.interviewStages || [];
    return stageOptions.reduce((acc, stage) => {
      acc[stage.id] = initialStages.includes(stage.id);
      return acc;
    }, {});
  };

  // フォームデータ
  const [formData, setFormData] = useState(() => {
    const saved = loadCommonFields();
    return {
      role: initialValues.role || saved.position || '',             // 職種
      traits: initialValues.traits || '',         // 求める人物像
      duties: initialValues.duties || '',         // 業務内容
      abstractNeeds: initialValues.abstractNeeds || '',  // 抽象的な要望
      interviewStages: initialValues.interviewStages || [],  // 面接段階
      candidateInfo: initialValues.candidateInfo || '',  // 候補者情報
      stageDetails: initialValues.stageDetails || {}     // 面接段階ごとの詳細情報
    };
  });

  // 面接段階のチェックボックス状態
  const [stageSelections, setStageSelections] = useState(initializeStageSelections());

  // Accordion展開状態
  const [openOptional, setOpenOptional] = useState(false);

  // チェックボックスの変更時に呼ばれるハンドラ
  const handleStageChange = (e) => {
    const { name, checked } = e.target;
    
    // チェックボックスの状態を更新
    setStageSelections(prev => ({
      ...prev,
      [name]: checked
    }));
    
    // フォームデータのinterviewStagesを更新
    setFormData(prev => {
      const updatedStages = Object.keys(stageSelections).filter(
        key => key === name ? checked : stageSelections[key]
      );
      
      return {
        ...prev,
        interviewStages: updatedStages
      };
    });
  };

  // 選択された面接段階が変更されたときに呼ばれるeffect
  useEffect(() => {
    const selectedStages = Object.keys(stageSelections).filter(key => stageSelections[key]);
    
    setFormData(prev => ({
      ...prev,
      interviewStages: selectedStages
    }));
  }, [stageSelections]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 面接段階の詳細情報の変更を処理するハンドラ
  const handleStageDetailChange = (stageId, field, value) => {
    setFormData(prev => {
      const updatedStageDetails = {
        ...prev.stageDetails,
        [stageId]: {
          ...(prev.stageDetails[stageId] || {}),
          [field]: value
        }
      };
      
      return {
        ...prev,
        stageDetails: updatedStageDetails
      };
    });
  };

  // "未入力なら赤背景、入力済みなら白"にする関数
  const requiredFieldStyle = (val) => {
    const strVal = typeof val === 'string' ? val.trim() : '';
    return {
      backgroundColor: strVal === '' ? adaptiveColor(theme, 'rgba(255,248,248,0.8)', 'rgba(60,30,30,0.8)') : adaptiveColor(theme, 'rgba(255,255,255,0.9)', 'rgba(30,30,30,0.9)'),
      borderRadius: '12px',
    };
  };

  // 必須項目が入力されているかチェック
  const canSubmit = () => {
    const isEmpty = (v) => !v || !v.trim();
    const hasSelectedStages = formData.interviewStages.length > 0;
    
    // 必須項目が埋まっているか
    if (
      isEmpty(formData.role) ||
      isEmpty(formData.traits) ||
      isEmpty(formData.duties) ||
      isEmpty(formData.abstractNeeds) ||
      !hasSelectedStages
    ) {
      return false;
    }
    return true;
  };

  // フォーム送信
  const handleSubmit = (e) => {
    e.preventDefault();

    // 必須項目チェック
    if (!canSubmit()) {
      showError(null, "必須項目が未入力です\n\n職種、求める人物像、業務内容、抽象的な要望、面接段階を入力してください");
      return;
    }

    // 共通プロフィールに保存
    saveCommonFields({ position: formData.role });

    // フォームデータにテンプレート情報を追加して親コンポーネントに渡す
    onSubmit({
      ...formData,
      templateId: selectedTemplate?.id || null
    });
  };

  return (
      <StyledPaper
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Box sx={{ position: 'relative', mb: 2 }}>
          {onBack && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              size="small"
              sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary', minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              戻る
            </Button>
          )}
          <FormTitle variant="h6" sx={{ mb: 0, textAlign: 'center' }}>
            面接対象ポジションの情報を入力
          </FormTitle>
        </Box>

      {/* 選択されたテンプレートの表示 */}
      {selectedTemplate && (
        <Alert
          severity="success"
          icon={<DescriptionIcon />}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              使用テンプレート: {selectedTemplate.template_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip label={selectedTemplate.job_category} size="small" color="primary" variant="outlined" />
              {selectedTemplate.experience_level && (
                <Chip label={selectedTemplate.experience_level} size="small" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              BARS（行動アンカー評価尺度）に基づく構造化面接が実施されます
            </Typography>
          </Box>
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* 職種 */}
        <StyledTextField
          label="職種 (必須)"
          name="role"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.role)
          }}
          value={formData.role}
          onChange={handleChange}
          placeholder="例: エンジニア, 営業, マーケティング担当など"
          helperText="面接を実施する職種・ポジション名を入力してください。例：フロントエンドエンジニア、営業マネージャー、データアナリスト等"
        />

        {/* 求める人物像 */}
        <StyledTextField
          label="求める人物像 (必須)"
          name="traits"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.traits)
          }}
          value={formData.traits}
          onChange={handleChange}
          placeholder="例: チームを元気づけられるような明るい人"
          helperText="採用したい人材の性格・価値観・働き方などを具体的に記載してください。例：チームワークを重視する方、自発的に行動できる方、顧客志向が強い方など"
        />

        {/* 業務内容 */}
        <StyledTextField
          label="業務内容 (必須)"
          name="duties"
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.duties)
          }}
          value={formData.duties}
          onChange={handleChange}
          placeholder="例: 新規プロダクトの開発/顧客との折衝など"
          helperText="日々の具体的な業務内容、責任範囲、使用するツールや技術などを記載してください。例：顧客データ分析、週次レポート作成、クライアント対応（1日3-5件）など"
        />

        {/* 抽象的な要望 */}
        <StyledTextField
          label="抽象的な要望 (必須)"
          name="abstractNeeds"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            style: requiredFieldStyle(formData.abstractNeeds)
          }}
          value={formData.abstractNeeds}
          onChange={handleChange}
          placeholder="例: チーム士気を高められる人, etc."
          helperText="スキルや経験以外で重視したい要素を記載してください。例：リーダーシップがある、困難な状況でも前向きに取り組める、新しいアイデアを積極的に提案できるなど"
        />

        {/* 面接段階 (カード形式) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: textColors(theme).secondary }}>
            面接段階 (必須)
          </Typography>
          
          <Grid container spacing={2}>
            {stageOptions.map((stage) => (
              <Grid item xs={6} sm={4} md={2.4} key={stage.id}>
                <StyledCard
                  component={motion.div}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setStageSelections(prev => ({
                      ...prev,
                      [stage.id]: !stageSelections[stage.id]
                    }));
                  }}
                  sx={{
                    border: stageSelections[stage.id] ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    backgroundColor: stageSelections[stage.id] ? 'rgba(25, 118, 210, 0.08)' : adaptiveColor(theme, 'rgba(255,255,255,0.9)', 'rgba(30,30,30,0.9)'),
                  }}
                >
                  {stageSelections[stage.id] && (
                    <CheckCircleIcon 
                      color="primary" 
                      sx={{ 
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        fontSize: '18px'
                      }}
                    />
                  )}
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="body1" fontWeight={stageSelections[stage.id] ? 'bold' : 'regular'}>
                      {stage.label}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
          
          {formData.interviewStages.length === 0 && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
              少なくとも1つの面接段階を選択してください
            </Typography>
          )}
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            実施予定の面接段階を選択してください。カードをクリックして選択/解除できます。例：1次面接（人事）、2次面接（現場責任者）、最終面接（役員）など
          </Typography>
        </Box>

        {/* 候補者情報 */}
        <StyledTextField
          label="候補者情報"
          name="candidateInfo"
          fullWidth
          sx={{ mb: 2 }}
          value={formData.candidateInfo}
          onChange={handleChange}
          placeholder="例: スキルや経歴要約など"
          helperText="応募者の経歴、スキル、資格、これまでの実績などを記載してください。例：Python開発5年、プロジェクトマネジメント経験3年、TOEIC800点、前職で売上20%向上に貢献など"
        />

        {/* 面接段階ごとの詳細情報フォーム */}
        {formData.interviewStages.length > 0 && (
          <StyledAccordion
            expanded={openOptional}
            onChange={() => setOpenOptional(!openOptional)}
            sx={{ mt: 3 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {openOptional ? '面接段階ごとの詳細情報(クリックで閉じる)' : '面接段階ごとの詳細情報(クリックで開く)'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {formData.interviewStages.map((stageId) => {
                const stageName = stageOptions.find(opt => opt.id === stageId)?.label || stageId;
                const stageDetail = formData.stageDetails[stageId] || {};
                
                return (
                  <Box key={stageId} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      {stageName}の詳細
                    </Typography>
                    
                    <StyledTextField
                      label={`${stageName}の詳細`}
                      fullWidth
                      multiline
                      rows={2}
                      sx={{ mb: 2 }}
                      value={stageDetail.details || ''}
                      onChange={(e) => handleStageDetailChange(stageId, 'details', e.target.value)}
                      placeholder="例: この段階での確認事項や進め方"
                      helperText="この面接段階で特に確認したい事項、質問内容、評価ポイントなどを記載してください。例：技術力の詳細確認、マネジメント経験の深掘り、カルチャーフィットの確認など"
                    />
                    
                    {stageId !== formData.interviewStages[formData.interviewStages.length - 1] && (
                      <Divider sx={{ my: 2 }} />
                    )}
                  </Box>
                );
              })}
            </AccordionDetails>
          </StyledAccordion>
        )}

        {/* ------ 送信ボタン ------ */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <SubmitButton 
            type="submit" 
            variant="contained" 
            disabled={!canSubmit()}
            component={motion.button}
            whileHover={{ scale: !canSubmit() ? 1 : 1.02 }}
            whileTap={{ scale: !canSubmit() ? 1 : 0.98 }}
          >
            AIでチェックシート生成
          </SubmitButton>
        </Box>
      </Box>
    </StyledPaper>
  );
}

export default InterviewForm; 